/**
 * Team challenge shapes (P3.5 + the challenge loop), mirroring the server's
 * views (backend services/challenge/TeamChallengeService.ts). The server
 * computes every number from canonical results; the client only displays them.
 */

import { practiceLanguageOption } from '../../config/practiceLanguages';

type AttemptMode = 'training' | 'testing' | 'any';

export type ChallengeCondition =
  | {
      kind: 'conversation_attempts';
      target: number;
      mode: AttemptMode;
      scenarioTag?: string;
      minOverall?: number;
    }
  | { kind: 'conversation_minutes'; target: number }
  | { kind: 'npc_level'; target: number; personaKey: string }
  /** Distinct days with completed practice (the same activity streaks count). */
  | { kind: 'practice_days'; target: number }
  /** Learn lessons completed, optionally within one module. */
  | { kind: 'learning_lessons'; target: number; moduleSlug?: string }
  /** Completed attempts conducted in a practice language. */
  | { kind: 'language_attempts'; target: number; language: string; mode: AttemptMode; minOverall?: number };

/** Stored scope: 'team' is the cooperative mode. */
export type GoalScope = 'individual' | 'team' | 'competitive';
export type ChallengeMode = 'individual' | 'cooperative' | 'competitive';

/** Condition kinds a team total can be formed over (reaching a level is personal). */
export const TEAM_AGGREGATABLE_KINDS: ReadonlyArray<ChallengeCondition['kind']> = [
  'conversation_attempts',
  'conversation_minutes',
  'practice_days',
  'learning_lessons',
  'language_attempts',
];

export interface ChallengeSummary {
  id: string;
  title: string;
  description: string | null;
  goalScope: GoalScope;
  mode: ChallengeMode;
  audience: { kind: 'workspace' } | { kind: 'circle'; circleId: string };
  /** null when the stored condition can no longer be read. */
  condition: ChallengeCondition | null;
  startsAt: string;
  endsAt: string;
  status: 'upcoming' | 'active' | 'ended' | 'archived';
  participantCount: number;
  joined: boolean;
  createdByMe: boolean;
}

export interface ParticipantProgress {
  value: number;
  /** Activity whose result is unknown: neither counted nor held against anyone. */
  unknown: number;
  completed: boolean;
}

/** One opted-in participant: display name or 'Anonymous', never a real name or id. */
export interface LeaderboardEntry {
  rank: number;
  name: string;
  value: number;
  completed: boolean;
  isMe: boolean;
}

/** An opted-in participant whose only activity is unknown: listed, never ranked. */
export interface UnrankedEntry {
  name: string;
  isMe: boolean;
}

/** The canonical badge recognising this challenge (shown wherever badges show). */
export interface ChallengeAchievement {
  badgeKey: string;
  earnedAt: string;
}

export interface ChallengeDetail extends ChallengeSummary {
  me: {
    joined: boolean;
    shareOnLeaderboard: boolean;
    progress: ParticipantProgress | null;
    achievement: ChallengeAchievement | null;
  };
  aggregate: {
    participants: number;
    withheld: boolean;
    teamTotal: number | null;
    teamUnknown: number | null;
    teamCompleted: boolean | null;
    completedCount: number | null;
  };
  leaderboard: LeaderboardEntry[];
  unranked: UnrankedEntry[];
  canManage: boolean;
}

export interface CreateChallengeRequest {
  title: string;
  description?: string | null;
  goalScope: GoalScope;
  condition: ChallengeCondition;
  startsAt: string;
  endsAt: string;
}

export const TEAM_TOTAL_MIN_PARTICIPANTS = 3;

export const MODE_LABEL: Record<ChallengeMode, string> = {
  individual: 'Personal goal',
  cooperative: 'Team goal (cooperative)',
  competitive: 'Competitive',
};

/** The unit a condition's progress is measured in. */
export function conditionUnit(c: ChallengeCondition): string {
  switch (c.kind) {
    case 'conversation_minutes':
      return 'minutes';
    case 'npc_level':
      return 'level';
    case 'practice_days':
      return 'days';
    case 'learning_lessons':
      return 'lessons';
    default:
      return 'attempts';
  }
}

const modeWord = (m: AttemptMode) => (m === 'testing' ? 'Testing ' : m === 'training' ? 'Development ' : '');
const scoreClause = (min?: number) => (min !== undefined ? ` scoring at least ${min}` : '');

/** Plain-language statement of a condition. */
export function describeCondition(c: ChallengeCondition | null): string {
  if (!c) return 'This challenge’s goal can no longer be read.';
  switch (c.kind) {
    case 'conversation_minutes':
      return `${c.target} minutes of completed conversations`;
    case 'npc_level':
      return `Reach level ${c.target} with ${c.personaKey}`;
    case 'practice_days':
      return `Practise on ${c.target} different ${c.target === 1 ? 'day' : 'days'}`;
    case 'learning_lessons':
      return `Complete ${c.target} Learn ${c.target === 1 ? 'lesson' : 'lessons'}${c.moduleSlug ? ` in “${c.moduleSlug}”` : ''}`;
    case 'language_attempts': {
      const label = practiceLanguageOption(c.language)?.label ?? c.language;
      return `${c.target} completed ${modeWord(c.mode)}attempts in ${label}${scoreClause(c.minOverall)}`;
    }
    default: {
      const tag = c.scenarioTag ? ` on scenarios tagged “${c.scenarioTag}”` : '';
      return `${c.target} completed ${modeWord(c.mode)}attempts${tag}${scoreClause(c.minOverall)}`;
    }
  }
}
